// client/src/coms/views/games_parts/VoiceWatchBanner.js
// Shown in a voice channel while a Watch Together session started there is running: what is playing, who is
// watching, and one click to join it in Activities. Joining needs no voice connection, and the session is
// the same one people outside the channel joined with its code.
import React, { useCallback, useEffect, useState } from 'react';
import { gamesApi } from './useGameSession';
import { setActiveGame } from './activeGameStore';

const POLL_MS = 15000;

export function openWatchSession(activity, serverId = '') {
  setActiveGame({ kind: 'watch', id: activity.id, name: activity.title || 'Watch Together', serverId });
  window.dispatchEvent(new CustomEvent('vox-open-games', { detail: { openActive: true } }));
}

export default function VoiceWatchBanner({ server, channel }) {
  const serverDbId = Number(server && (server.db_id != null ? server.db_id : server.server_id != null ? server.server_id : server.id)) || 0;
  const channelDbId = Number(channel && (channel.db_id != null ? channel.db_id : channel.id)) || 0;
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!serverDbId || !channelDbId) return;
    const result = await gamesApi(`/api/activities/watch?serverId=${serverDbId}`).catch(() => null);
    if (result && result.ok) setSessions((result.data.activities || []).filter((a) => a.voice && Number(a.voice.channelId) === channelDbId));
  }, [channelDbId, serverDbId]);

  useEffect(() => {
    setSessions([]);
    refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  if (!sessions.length) return null;
  return (
    <div className="vox-voice-watch" role="region" aria-label="Watch Together in this channel">
      {sessions.map((a) => (
        <div key={a.id} className="vox-voice-watch-row" data-testid="voice-watch-banner">
          <span aria-hidden="true" style={{ fontSize: 18 }}>📺</span>
          <span className="vox-voice-watch-text">
            <strong>{a.title || 'Watch Together'}</strong> is running in this channel
            <span className="vox-voice-watch-meta">
              {a.watchingCount} watching · hosted by {a.hostName}{a.nowPlaying && a.nowPlaying.title ? ` · ${a.nowPlaying.title}` : ''}
            </span>
          </span>
          <button
            type="button"
            className="coms-mini-btn primary"
            data-testid="voice-watch-join"
            onClick={async () => {
              setError('');
              if (!a.watching) {
                const joined = await gamesApi(`/api/activities/watch/${encodeURIComponent(a.id)}/join`, { method: 'POST', body: {} }).catch(() => null);
                if (!joined || !joined.ok) {
                  setError('Could not join this watch session.');
                  return;
                }
              }
              openWatchSession(a, String(serverDbId));
            }}
          >
            {a.watching ? 'Open' : 'Join'}
          </button>
        </div>
      ))}
      {error ? <div role="alert" className="coms-form-error">{error}</div> : null}
    </div>
  );
}
